<script setup>
import { useTemplateRef, onBeforeUnmount, computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTrack } from 'dashboard/composables';
import { vOnClickOutside } from '@vueuse/components';
import { CONTACTS_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { useContactFilterContext } from './contactProvider.js';

import Button from 'next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import FilterGroup from './FilterGroup.vue';

const props = defineProps({
  isSegmentView: { type: Boolean, default: false },
  segmentName: { type: String, default: '' },
});

const emit = defineEmits([
  'applyFilter',
  'updateSegment',
  'close',
  'clearFilters',
]);
const { filterTypes } = useContactFilterContext();

const groups = defineModel({
  type: Array,
  default: () => [],
});
const segmentNameLocal = ref(props.segmentName);

const DEFAULT_CONDITION = {
  attributeKey: 'name',
  filterOperator: 'equal_to',
  values: '',
  queryOperator: 'and',
  attributeModel: 'standard',
};

const DEFAULT_GROUP = () => ({
  conditions: [{ ...DEFAULT_CONDITION }],
  groupOperator: 'AND',
});

if (!groups.value || groups.value.length === 0) {
  groups.value = [DEFAULT_GROUP()];
}

const { t } = useI18n();

const resetFilter = () => {
  emit('clearFilters');
  groups.value = [DEFAULT_GROUP()];
};

const addGroup = () => {
  groups.value.push(DEFAULT_GROUP());
};

const removeGroup = index => {
  if (groups.value.length > 1) {
    groups.value.splice(index, 1);
  } else {
    resetFilter();
  }
};

const groupRefs = useTemplateRef('groupRefs');

const isConditionsValid = () => {
  if (!groupRefs.value) return true;
  return groupRefs.value.every(group => group && group.validate());
};

const updateSavedSegment = () => {
  if (isConditionsValid()) {
    emit('updateSegment', groups.value, segmentNameLocal.value);
  }
};

function validateAndSubmit() {
  if (!isConditionsValid()) return;

  emit('applyFilter', groups.value);
  useTrack(CONTACTS_EVENTS.APPLY_FILTER, {
    appliedFilters: groups.value.flatMap(g =>
      g.conditions.map(c => ({
        key: c.attributeKey,
        operator: c.filterOperator,
        queryOperator: c.queryOperator,
      }))
    ),
  });
}

const filterModalHeaderTitle = computed(() => {
  return !props.isSegmentView
    ? t('CONTACTS_LAYOUT.FILTER.TITLE')
    : t('CONTACTS_LAYOUT.FILTER.EDIT_SEGMENT');
});

onBeforeUnmount(() => emit('close'));
const outsideClickHandler = [
  () => emit('close'),
  { ignore: ['#toggleContactsFilterButton'] },
];
</script>

<template>
  <div
    v-on-click-outside="outsideClickHandler"
    class="z-40 max-w-3xl min-w-96 lg:w-[750px] overflow-visible w-full border border-n-weak bg-n-alpha-3 backdrop-blur-[100px] shadow-lg rounded-xl p-6 grid gap-6"
  >
    <h3 class="text-base font-medium leading-6 text-n-slate-12">
      {{ filterModalHeaderTitle }}
    </h3>
    <div v-if="props.isSegmentView">
      <div class="pb-6 border-b border-n-weak">
        <Input
          v-model="segmentNameLocal"
          :label="$t('CONTACTS_LAYOUT.FILTER.SEGMENT.LABEL')"
          :placeholder="t('CONTACTS_LAYOUT.FILTER.SEGMENT.INPUT_PLACEHOLDER')"
        />
      </div>
    </div>

    <div class="grid gap-3">
      <FilterGroup
        v-for="(group, index) in groups"
        ref="groupRefs"
        :key="index"
        v-model:conditions="group.conditions"
        v-model:group-operator="group.groupOperator"
        :group-index="index"
        :total-groups="groups.length"
        :filter-types="filterTypes"
        @remove-group="removeGroup(index)"
      />
    </div>

    <div class="flex justify-between gap-2">
      <Button sm ghost blue class="flex-shrink-0" @click="addGroup">
        {{ $t('CONTACTS_LAYOUT.FILTER.BUTTONS.ADD_GROUP') }}
      </Button>
      <div class="flex gap-2 flex-shrink-0">
        <Button sm faded slate @click="resetFilter">
          {{ $t('CONTACTS_LAYOUT.FILTER.BUTTONS.CLEAR_FILTERS') }}
        </Button>
        <Button
          v-if="isSegmentView"
          sm
          solid
          blue
          :disabled="!segmentNameLocal"
          @click="updateSavedSegment"
        >
          {{ $t('CONTACTS_LAYOUT.FILTER.BUTTONS.UPDATE_SEGMENT') }}
        </Button>
        <Button v-else sm solid blue @click="validateAndSubmit">
          {{ $t('CONTACTS_LAYOUT.FILTER.BUTTONS.APPLY_FILTERS') }}
        </Button>
      </div>
    </div>
  </div>
</template>
